import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError, api } from "@/lib/api";
import { ShieldStar, GoogleLogo, Sparkle } from "@phosphor-icons/react";

const LOGIN_BG = "https://static.prod-images.emergentagent.com/jobs/4545735e-77e3-490b-85fb-3bc856dbec8e/images/42cf850309487a45d6e166da5a1098cdf155d58108992719798f0d879d75e5be.png";

// Allow only same-app routes to be passed in ?next= — prevents open-redirect.
const safeNext = (n) => (n && n.startsWith("/") && !n.startsWith("//") ? n : "/dashboard");

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const prefillEmail = params.get("prefill_email") || "";
  const { login, loginDemo } = useAuth();
  const [email, setEmail] = useState(prefillEmail || "pankaj@span.com");
  const [password, setPassword] = useState(prefillEmail ? "" : "SpanCEO@2026");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav(next);
    } catch (e) {
      // Structured error surfacing — the old "Something went wrong" swallowed
      // real causes (server down, network, wrong pw, locked account).
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (!e?.response) {
        // No response = network/DNS/CORS/backend-restarting (502/503 also often
        // land here after Cloudflare retry).
        msg = "Can't reach the server right now. The backend may be restarting — please retry in 15 seconds. If this persists, contact your admin.";
      } else if (status === 401) {
        msg = detail
          ? formatApiError(detail)
          : `The password for ${email} is incorrect. Use "Forgot password?" below if you don't remember it.`;
      } else if (status === 403) {
        msg = formatApiError(detail) || `Your account is deactivated. Contact your admin.`;
      } else if (status === 423 || (status === 429 && String(detail || "").toLowerCase().includes("lock"))) {
        msg = formatApiError(detail) || `Too many failed attempts — account locked for 15 minutes. Use "Forgot password?" to reset immediately.`;
      } else if (status === 429) {
        msg = "Too many requests — wait a minute and try again.";
      } else if (status >= 500) {
        msg = `Server error (${status}). This has been logged — please retry in 30 seconds.`;
      } else {
        msg = formatApiError(detail) || `Sign-in failed (${status || "network"}).`;
      }
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const tryDemo = async () => {
    setErr(""); setLoading(true);
    try {
      await loginDemo();
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Demo login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5" data-testid="login-page">
      {/* Left: Form */}
      <div className="lg:col-span-2 bg-white flex flex-col">
        <div className="px-10 pt-10">
          <img
            src="/api/static/brand/C2_Horizontal_White.svg"
            alt="SPAN CasePrime"
            className="h-10 w-auto"
            style={{ maxWidth: "280px" }}
            data-testid="brand-logo-login"
          />
          <div className="text-[12px] text-[#918F90] mt-2 font-mono uppercase tracking-[0.16em]">Brand Protection Case Management</div>
        </div>

        <div className="flex-1 flex items-center px-10">
          <div className="w-full max-w-[380px]">
            <div className="label-eyebrow mb-4">Secure Access · Tier 1</div>
            <h1 className="font-display text-[34px] font-bold leading-[1.05] tracking-tight">
              Operational command<br/>for brand protection.
            </h1>
            <p className="text-[13px] text-[var(--span-text-2)] mt-3 leading-relaxed">
              Court-admissible evidence chains, multi-jurisdictional case management,
              and the SPAN Framework™—operationalized.
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4">
              <div>
                <label className="label-eyebrow block mb-1.5">Email</label>
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="span-input font-mono text-[13px]"
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Password</label>
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="span-input font-mono text-[13px]"
                  autoComplete="current-password"
                />
              </div>
              {err && <div className="text-[12px] text-[var(--span-danger)]" data-testid="login-error">{err}</div>}
              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="span-btn w-full disabled:opacity-50"
              >
                {loading ? "Authenticating…" : "Sign in"}
              </button>

              <div className="text-center pt-1">
                <Link to="/auth/forgot"
                      data-testid="login-forgot-link"
                      className="text-[12px] text-[var(--span-text-2)] hover:text-[var(--brand-primary)] hover:underline">
                  Forgot password?
                </Link>
              </div>

              <div className="flex items-center gap-3 my-2">
                <div className="h-px bg-[var(--span-border)] flex-1" />
                <span className="text-[10px] tracking-[0.18em] text-[var(--span-text-2)] uppercase">or</span>
                <div className="h-px bg-[var(--span-border)] flex-1" />
              </div>

              <button
                type="button"
                data-testid="google-login-button"
                onClick={googleLogin}
                className="span-btn span-btn-ghost w-full"
              >
                <GoogleLogo size={16} weight="bold" /> Continue with Google
              </button>

              <button
                type="button"
                data-testid="try-demo-button"
                onClick={tryDemo}
                disabled={loading}
                className="span-btn span-btn-ghost w-full text-[var(--span-text-2)] border-dashed"
              >
                <Sparkle size={14} weight="bold" /> Try the live demo (no signup)
              </button>
            </form>

            <div className="mt-8 text-[11px] text-[var(--span-text-2)] font-mono">
              demo · pankaj@span.com / SpanCEO@2026
            </div>
          </div>
        </div>

        <div className="px-10 py-6 border-t border-[var(--span-border)] flex items-center justify-between text-[10px] text-[var(--span-text-2)]">
          <span className="font-mono">© SPAN CasePrime · 2026</span>
          <span className="font-mono">ISO 27035 · CHAIN-OF-CUSTODY READY</span>
        </div>
      </div>

      {/* Right: Visual */}
      <div className="hidden lg:flex lg:col-span-3 relative bg-black overflow-hidden">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-black/10" />
        <div className="relative z-10 flex flex-col justify-end p-14 text-white w-full">
          <div className="grid grid-cols-3 gap-px bg-white/10 mb-12 border border-white/10">
            {[
              { k: "JURISDICTIONS", v: "9" },
              { k: "CASE CODES", v: "13" },
              { k: "CURRENCIES", v: "9" },
            ].map((m) => (
              <div key={m.k} className="bg-black/40 backdrop-blur-sm p-5">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase opacity-60">{m.k}</div>
                <div className="font-display text-[40px] font-bold mt-1 tnum">{m.v}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <ShieldStar size={20} weight="bold" />
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase opacity-80">
              SPAN FRAMEWORK™ · OPERATIONAL EXCELLENCE
            </div>
          </div>
          <div className="font-display font-bold text-[44px] leading-[1.05] mt-2 max-w-xl">
            From signal to seizure to settlement.
          </div>
        </div>
      </div>
    </div>
  );
}
